import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
} from "recharts";

import {
  Card,
  PageHeader,
  StatCard,
  Tabs,
  Badge,
  RISK_BADGE_TONES,
  CHART_COLORS,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminPortfolioHook } from "../../hooks/useAdminHooks";
import { useManagerPortfolioHook } from "../../hooks/useManagerHooks";
import { RISK_LABELS, formatUGX } from "../../data/mockData";
import "./portfolio.css";

// Admin / Program Manager — Portfolio Performance
// CSS class prefix: `adminPortfolio-`

const TABS = [
  { value: "breakdown", label: "Performance Breakdown" },
  { value: "growth", label: "Growth & Stability" },
  { value: "capital", label: "Capital & Returns" },
];

const ICONS_BY_NAME = {
  "building-2": Building2,
  "dollar-sign": DollarSign,
  "trending-up": TrendingUp,
  target: Target,
  "trending-down": TrendingDown,
};

const DEFAULT_PORTFOLIO_DATA = {
  summary_cards: [],
  performance_breakdown: {
    revenue_by: {
      cohort: [],
      graduation_level: [],
      sector: [],
      gender_classification: [],
    },
  },
  growth_and_stability: {
    growth_comparison: [],
    consistent_growth: [],
    stagnating_or_declining: [],
  },
  capital_and_returns: {
    capital_vs_revenue: [],
    repayment: {
      performance: [],
      risk: [],
    },
  },
  ventures_performance: [],
};

function GrowthIcon({ value }) {
  if (value > 0)
    return (
      <ArrowUpRight className="adminPortfolio-trendIcon admin-text-success" />
    );
  if (value < 0)
    return (
      <ArrowDownRight className="adminPortfolio-trendIcon admin-text-danger" />
    );
  return <Minus className="adminPortfolio-trendIcon admin-muted" />;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [tab, setTab] = useState("breakdown");
  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  const workspaceBase = user?.role === ROLES.ADMIN ? "/admin" : "/manager";
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;
  const shouldFetchApi = isAdmin || isManager;

  const adminPortfolioQuery = useAdminPortfolioHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerPortfolioQuery = useManagerPortfolioHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const portfolioApiData = isManager
    ? managerPortfolioQuery.data
    : adminPortfolioQuery.data;
  const isLoading = isManager
    ? managerPortfolioQuery.isLoading
    : adminPortfolioQuery.isLoading;

  const portfolioData = portfolioApiData ?? DEFAULT_PORTFOLIO_DATA;
  const summaryCards = portfolioData?.summary_cards ?? [];

  // ---------- Breakdown charts ----------
  const revenueByCohort = useMemo(
    () =>
      (portfolioData?.performance_breakdown?.revenue_by?.cohort ?? []).map(
        (row) => ({
          name: row.period ?? "Unknown",
          value: Number(row.value ?? 0),
        }),
      ),
    [portfolioData?.performance_breakdown?.revenue_by?.cohort],
  );

  const revenueByLevel = useMemo(
    () =>
      (
        portfolioData?.performance_breakdown?.revenue_by?.graduation_level ?? []
      ).map((row) => ({
        name: `Level ${row.level ?? "-"}`,
        value: Number(row.value ?? 0),
      })),
    [portfolioData?.performance_breakdown?.revenue_by?.graduation_level],
  );

  const revenueBySector = useMemo(
    () =>
      (portfolioData?.performance_breakdown?.revenue_by?.sector ?? []).map(
        (row) => ({
          name: row.name ?? "-",
          value: Number(row.value ?? 0),
        }),
      ),
    [portfolioData?.performance_breakdown?.revenue_by?.sector],
  );

  const revenueByGender = useMemo(
    () =>
      (
        portfolioData?.performance_breakdown?.revenue_by
          ?.gender_classification ?? []
      ).map((row, index) => ({
        name: row.classification ?? "-",
        value: Number(row.value ?? 0),
        fill: CHART_COLORS[index % CHART_COLORS.length],
      })),
    [portfolioData?.performance_breakdown?.revenue_by?.gender_classification],
  );

  // ---------- Growth tab ----------
  const growthCompare = useMemo(
    () =>
      (portfolioData?.growth_and_stability?.growth_comparison ?? []).map(
        (venture) => ({
          name:
            (venture.venture_name ?? "").length > 14
              ? `${venture.venture_name.slice(0, 14)}…`
              : (venture.venture_name ?? "-"),
          "3 Month": Number(venture.growth_3_month ?? 0),
          "6 Month": Number(venture.growth_6_month ?? 0),
        }),
      ),
    [portfolioData?.growth_and_stability?.growth_comparison],
  );

  const consistent =
    portfolioData?.growth_and_stability?.consistent_growth ?? [];
  const stagnating =
    portfolioData?.growth_and_stability?.stagnating_or_declining ?? [];

  // ---------- Capital tab ----------
  const capitalVsRevenue = useMemo(
    () =>
      (portfolioData?.capital_and_returns?.capital_vs_revenue ?? []).map(
        (venture) => ({
          name:
            (venture.venture_name ?? "").length > 14
              ? `${venture.venture_name.slice(0, 14)}…`
              : (venture.venture_name ?? "-"),
          capital: Number(venture.capital ?? 0),
          revenue: Number(venture.revenue ?? 0),
        }),
      ),
    [portfolioData?.capital_and_returns?.capital_vs_revenue],
  );

  const repaymentBreakdown = useMemo(() => {
    const colorByStatus = {
      "On Track": "hsl(181, 57%, 42%)",
      Behind: "hsl(var(--destructive))",
      Completed: "hsl(64, 53%, 46%)",
      "N/A": "hsl(220, 14%, 60%)",
    };
    return (
      portfolioData?.capital_and_returns?.repayment?.performance ?? []
    ).map((row) => ({
      name: row.status ?? "N/A",
      value: Number(row.value ?? 0),
      fill: colorByStatus[row.status] ?? "hsl(220, 14%, 60%)",
    }));
  }, [portfolioData?.capital_and_returns?.repayment?.performance]);

  const riskVentures =
    portfolioData?.capital_and_returns?.repayment?.risk ?? [];

  const venturesPerformance = portfolioData?.ventures_performance ?? [];

  // ---------- Sortable table ----------
  const sorted = useMemo(() => {
    const get = (venture) => {
      switch (sortColumn) {
        case "growth":
          return Number(venture.growth_rate ?? 0);
        case "score":
          return Number(venture.performance_score ?? 0);
        case "level":
          return Number(venture.graduation_level?.level ?? 0);
        case "revenue":
        default:
          return Number(venture.monthly_revenue ?? 0);
      }
    };
    return [...venturesPerformance].sort((firstVenture, secondVenture) =>
      sortDir === "asc"
        ? get(firstVenture) - get(secondVenture)
        : get(secondVenture) - get(firstVenture),
    );
  }, [venturesPerformance, sortColumn, sortDir]);

  const onSort = (col) => {
    if (sortColumn === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortColumn(col);
      setSortDir("desc");
    }
  };

  const sortIcon = (col) =>
    sortColumn === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  if (isLoading && shouldFetchApi) {
    return (
      <div className="adminPortfolio-root">
        <PageHeader
          title="Portfolio Performance"
          description="Deep-dive analysis of venture and portfolio performance over time"
        />
        <p className="admin-muted">Loading portfolio data...</p>
      </div>
    );
  }

  return (
    <div className="adminPortfolio-root">
      <PageHeader
        title="Portfolio Performance"
        description="Deep-dive analysis of venture and portfolio performance over time"
      />

      <div className="admin-grid admin-grid-5">
        {summaryCards.map((card, idx) => {
          const Icon = ICONS_BY_NAME[card.icon] ?? Building2;
          return (
            <StatCard
              key={`${card.title}-${idx}`}
              title={card.title}
              value={card.value}
              icon={Icon}
              description={card.label}
            />
          );
        })}
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "breakdown" && (
        <div className="admin-grid admin-grid-2">
          <Card title="Revenue by Cohort">
            <div className="adminPortfolio-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByCohort}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis
                    tickFormatter={(value) =>
                      `${(value / 1_000_000).toFixed(0)}M`
                    }
                    fontSize={10}
                  />
                  <Tooltip formatter={(value) => formatUGX(value)} />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Revenue by Graduation Level">
            <div className="adminPortfolio-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByLevel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis
                    tickFormatter={(value) =>
                      `${(value / 1_000_000).toFixed(0)}M`
                    }
                    fontSize={10}
                  />
                  <Tooltip formatter={(value) => formatUGX(value)} />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[1]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Revenue by Sector">
            <div className="adminPortfolio-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueBySector} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(value) =>
                      `${(value / 1_000_000).toFixed(0)}M`
                    }
                    fontSize={10}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    fontSize={11}
                  />
                  <Tooltip formatter={(value) => formatUGX(value)} />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[2]}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Revenue by Gender Classification">
            <div className="adminPortfolio-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={revenueByGender}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  />
                  <Tooltip formatter={(value) => formatUGX(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {tab === "growth" && (
        <>
          <Card title="3 vs 6 Month Growth Comparison">
            <div className="adminPortfolio-chart adminPortfolio-chart-tall">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growthCompare}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis tickFormatter={(value) => `${value}%`} fontSize={10} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar
                    dataKey="3 Month"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="6 Month"
                    fill={CHART_COLORS[2]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="admin-grid admin-grid-2">
            <Card title={`Consistent Growth (${consistent.length})`}>
              <div className="adminPortfolio-list">
                {consistent.map((venture) => (
                  <button
                    key={venture.venture_id}
                    type="button"
                    className="adminPortfolio-listItem adminPortfolio-listItem-success"
                    onClick={() =>
                      navigate(
                        `${workspaceBase}/ventures/${venture.venture_id}`,
                      )
                    }
                  >
                    <span className="adminPortfolio-listName">
                      {venture.venture_name}
                    </span>
                    <span className="adminPortfolio-listValue admin-text-success">
                      +{Number(venture.value ?? 0)}%{" "}
                      <ArrowUpRight className="adminPortfolio-trendIcon" />
                    </span>
                  </button>
                ))}
                {consistent.length === 0 && (
                  <p className="admin-muted">No ventures in this group.</p>
                )}
              </div>
            </Card>

            <Card title={`Stagnating / Declining (${stagnating.length})`}>
              <div className="adminPortfolio-list">
                {stagnating.map((venture) => {
                  const growth = Number(venture.value ?? 0);
                  return (
                    <button
                      key={venture.venture_id}
                      type="button"
                      className="adminPortfolio-listItem adminPortfolio-listItem-warning"
                      onClick={() =>
                        navigate(
                          `${workspaceBase}/ventures/${venture.venture_id}`,
                        )
                      }
                    >
                      <span className="adminPortfolio-listName">
                        {venture.venture_name}
                      </span>
                      <span
                        className={
                          "adminPortfolio-listValue " +
                          (growth < 0
                            ? "admin-text-danger"
                            : "admin-text-warning")
                        }
                      >
                        {growth}% <GrowthIcon value={growth} />
                      </span>
                    </button>
                  );
                })}
                {stagnating.length === 0 && (
                  <p className="admin-muted">No ventures in this group.</p>
                )}
              </div>
            </Card>
          </div>
        </>
      )}

      {tab === "capital" && (
        <>
          <div className="admin-grid admin-grid-2">
            <Card title="Capital Deployed vs Revenue Generated">
              <div className="adminPortfolio-chart adminPortfolio-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={capitalVsRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={10} />
                    <YAxis
                      tickFormatter={(value) =>
                        `${(value / 1_000_000).toFixed(0)}M`
                      }
                      fontSize={10}
                    />
                    <Tooltip formatter={(value) => formatUGX(value)} />
                    <Legend />
                    <Bar
                      dataKey="capital"
                      name="Capital"
                      fill={CHART_COLORS[0]}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="revenue"
                      name="Revenue"
                      fill={CHART_COLORS[1]}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card title="Repayment Performance">
              <div className="adminPortfolio-chart adminPortfolio-chart-tall">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={repaymentBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card title={`Ventures at Repayment Risk (${riskVentures.length})`}>
            {riskVentures.length === 0 ? (
              <p className="admin-muted">
                No ventures currently behind on repayment.
              </p>
            ) : (
              <div className="admin-grid admin-grid-2">
                {riskVentures.map((venture) => (
                  <button
                    key={venture.venture_id}
                    type="button"
                    className="adminPortfolio-riskCard"
                    onClick={() =>
                      navigate(
                        `${workspaceBase}/ventures/${venture.venture_id}`,
                      )
                    }
                  >
                    <div>
                      <p className="adminPortfolio-riskName">
                        {venture.venture_name}
                      </p>
                      <p className="admin-muted adminPortfolio-riskMeta">
                        {venture.sector}
                      </p>
                    </div>
                    <Badge tone="danger">{venture.repayment_status}</Badge>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Sortable performance table — always visible */}
      <Card title="All Ventures Performance">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Venture Name</th>
                <th
                  className="admin-th-sortable"
                  onClick={() => onSort("level")}
                >
                  Graduation Level{sortIcon("level")}
                </th>
                <th
                  className="admin-th-sortable"
                  onClick={() => onSort("revenue")}
                >
                  Monthly Revenue{sortIcon("revenue")}
                </th>
                <th
                  className="admin-th-sortable"
                  onClick={() => onSort("growth")}
                >
                  Growth Rate{sortIcon("growth")}
                </th>
                <th
                  className="admin-th-sortable"
                  onClick={() => onSort("score")}
                >
                  Performance Score{sortIcon("score")}
                </th>
                <th>Risk Level</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((venture) => {
                const growth = Number(venture.growth_rate ?? 0);
                const risk = String(
                  venture.risk_level ?? "medium",
                ).toLowerCase();
                return (
                  <tr
                    key={venture.venture_id}
                    className="admin-tr-clickable"
                    onClick={() =>
                      navigate(
                        `${workspaceBase}/ventures/${venture.venture_id}`,
                      )
                    }
                  >
                    <td>
                      <strong>{venture.venture_name}</strong>
                    </td>
                    <td>
                      <Badge tone="outline">
                        {venture.graduation_level?.stage ?? "-"} (L
                        {venture.graduation_level?.level ?? "-"})
                      </Badge>
                    </td>
                    <td>{formatUGX(Number(venture.monthly_revenue ?? 0))}</td>
                    <td>
                      <span className="adminPortfolio-trendCell">
                        <GrowthIcon value={growth} />
                        <span
                          className={
                            growth > 0
                              ? "admin-text-success"
                              : growth < 0
                                ? "admin-text-danger"
                                : ""
                          }
                        >
                          {growth}%
                        </span>
                      </span>
                    </td>
                    <td>
                      {venture.performance_score != null ? (
                        <>
                          <strong>{venture.performance_score}</strong>
                          <span className="admin-muted">/100</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Badge tone={RISK_BADGE_TONES[risk] ?? "muted"}>
                        {RISK_LABELS[risk] ?? venture.risk_level ?? "-"}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
